import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { CheckPoint } from 'interface'
import { DivIcon, Icon, IconOptions, Map } from 'leaflet'
import { useEffect, useMemo, useState } from 'react'
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMapEvents,
} from 'react-leaflet'
import './leaflet.css'
import {
  iconChecked,
  iconCheckpoint,
  iconEditing,
  iconLocation,
} from './MakeIcon'

const zoom = 17

interface LocationProps {
  icon?: Icon<IconOptions> | DivIcon
  position?: { lat: number; lng: number }
  checkpoint?: CheckPoint
  setPosition?: (position: { lat: number; lng: number }) => void
}

function LocationMarker({
  position,
  checkpoint,
  setPosition,
  icon = iconCheckpoint,
}: LocationProps) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng
      setPosition &&
        setPosition({
          lat,
          lng,
        })
    },
  })
  return position ? (
    <Marker position={position} icon={icon}>
      {checkpoint && (
        <Popup closeOnClick position={position} offset={[0, -40]}>
          {checkpoint.name}
        </Popup>
      )}
    </Marker>
  ) : null
}

interface Props {
  center?: CheckPoint
  canEdit?: boolean
  error?: boolean
  positions: CheckPoint[]
  useEdit?: () => void
  addPosition?: (val: { lat: number; lng: number }) => void
  updatePosition?: (val: { lat: number; lng: number }) => void
  released?: boolean
}
export default function OpenStressMap({
  center,
  positions,
  error,
  canEdit = true,
  addPosition,
  updatePosition,
  useEdit,
  released,
}: Props) {
  const [map, setMap] = useState<Map | null>(null)
  const [curPos, setCurPos] = useState<{ lat: number; lng: number }>()
  const [position, setPosition] = useState<{
    lat: number
    lng: number
  }>()
  useEffect(() => {
    getCurrentPosition()
      .then((pos) => {
        setCurPos(pos)
        if (canEdit) {
          if (center) {
            setPosition({
              lat: center.lat,
              lng: center.lng,
            })
          } else {
            setPosition(pos)
          }
        }
      })
      .catch(() => {
        setCurPos({
          lat: 35.6836157,
          lng: 139.783111,
        })
        setPosition({
          lat: 35.6836157,
          lng: 139.783111,
        })
      })
  }, [canEdit, center])
  useEffect(() => {
    position && addPosition && addPosition(position)
  }, [addPosition, position])
  const displayMap = useMemo(() => {
    return (
      <MapContainer
        center={center || curPos}
        zoom={zoom}
        // dragging={canEdit}
        scrollWheelZoom={false}
        style={{ width: '100%', height: '80vh' }}
        ref={setMap}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {canEdit && (
          <LocationMarker
            position={position}
            setPosition={(pos) => setPosition(pos)}
          />
        )}

        <LocationMarker position={curPos} icon={iconLocation} />
        {positions.map((v, i) => {
          return (
            <LocationMarker
              key={i}
              icon={iconChecked}
              checkpoint={v}
              position={v}
            />
          )
        })}
        {!canEdit && center && (
          <LocationMarker
            icon={iconEditing}
            checkpoint={center}
            position={center}
          />
        )}
      </MapContainer>
    )
  }, [canEdit, center, curPos, position, positions])
  if (!curPos) return null
  const moveCurrentLocation = () => {
    getCurrentPosition().then((pos) => {
      map?.setView(pos, zoom)
      setCurPos(pos)
      setPosition(pos)
    })
  }
  return (
    <Paper elevation={4}>
      <Box
        borderRadius={2}
        overflow="hidden"
        width="100%"
        my={2}
        sx={{
          border: '2px solid',
          borderColor: error ? 'red' : '#0000',
        }}
      >
        {displayMap}
        {!released ? (
          <Stack
            my={1}
            px={1}
            spacing={2}
            direction={{
              xs: 'column',
              md: 'row',
            }}
          >
            {canEdit ? (
              <>
                {center && (
                  <Button color="warning" variant="outlined" onClick={useEdit}>
                    キャンセル
                  </Button>
                )}
                <Button
                  disabled={
                    curPos.lat === position?.lat && curPos.lng === position.lng
                  }
                  variant="outlined"
                  onClick={moveCurrentLocation}
                >
                  現在地に移動
                </Button>
              </>
            ) : (
              useEdit && (
                <>
                  <Button
                    disabled={
                      !center &&
                      curPos.lat === position?.lat &&
                      curPos.lng === position.lng
                    }
                    color="info"
                    variant="contained"
                    onClick={useEdit}
                  >
                    修正する
                  </Button>
                </>
              )
            )}
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              flex={1}
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              {canEdit && (
                <>
                  <Stack
                    direction={{
                      xs: 'column',
                      md: 'row',
                    }}
                    spacing={2}
                  >
                    <Typography>北緯: {position?.lat || '?'}</Typography>
                    <Typography>東経: {position?.lng || '?'}</Typography>
                  </Stack>
                  {!!updatePosition && (
                    <Button
                      disabled={
                        !center &&
                        curPos.lat === position?.lat &&
                        curPos.lng === position.lng
                      }
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        position && updatePosition && updatePosition(position)
                      }}
                    >
                      決定する
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        ) : (
          ''
        )}
      </Box>
    </Paper>
  )
}

function getCurrentPosition() {
  return new Promise<{
    lat: number
    lng: number
  }>((rs, rj) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          rs(pos)
        },
        () => rj('error')
      )
    } else {
      rj('error')
    }
  })
}
